import React from 'react';
import { Link } from 'react-router-dom';
import SiteFooter from './SiteFooter';
import wordmark from './images/voidmaw-wordmark.png';
import './Othercrown.css';

// Both press kits deliberately share every layout, color and typography rule.
// Each page supplies only its content; sections with nothing to show are skipped.

// Splits media into rows of the given sizes, repeating the pattern as needed.
function toRows(items, pattern) {
  const rows = [];
  for (let start = 0, step = 0; start < items.length; step++) {
    const size = pattern[step % pattern.length];
    rows.push(items.slice(start, start + size));
    start += size;
  }
  return rows;
}

function MediaRows({ items, pattern, render }) {
  return toRows(items, pattern).map(row => <div className={`pk-row pk-row-${row.length}`} key={row[0].original || row[0].src || row[0].youtube}>{row.map(render)}</div>);
}

function Asset(item) {
  return <a className={`pk-asset${item.light ? ' pk-asset-light' : ''}`} href={item.original} download={item.download || true} key={item.original}><img src={item.src} alt={item.alt || item.label} loading="lazy" /></a>;
}

function CaptionedAsset(item) {
  return <a className={`pk-asset${item.light ? ' pk-asset-light' : ''}`} href={item.original} download={item.download || true} key={item.original}><img src={item.src} alt={item.alt || item.label} loading="lazy" /><span>{item.label}</span></a>;
}

function Video(video) {
  if (video.youtube) return <div className="pk-video" key={video.youtube}><iframe src={`https://www.youtube.com/embed/${video.youtube}`} title={video.title} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /><span className="pk-caption">{video.title}</span></div>;
  return <div className="pk-video" key={video.src}><video src={video.src} poster={video.poster} aria-label={video.label} controls muted loop playsInline preload="none" /><a className="pk-caption" href={video.src} download>{video.label}</a></div>;
}

function SectionHeading({ id, title, Tag = 'h2' }) {
  return <React.Fragment><Tag id={id}>{title}</Tag><hr className="pk-rule" /></React.Fragment>;
}

function DownloadAll({ href, spaced = false }) {
  return <p className={`pk-download-all${spaced ? ' pk-download-spaced' : ''}`}><span aria-hidden="true">📥</span> <a href={href} target="_blank" rel="noopener noreferrer">Download all assets from the Drive here</a> <span aria-hidden="true">📥</span></p>;
}

function Release({ href, title, text, image }) {
  return <a className="pk-release" href={href} target="_blank" rel="noopener noreferrer" key={href}><img src={image} alt="" loading="lazy" /><span><img className="pk-release-source" src="https://cdn.uc.assets.prezly.com/8b6e831b-3539-4390-9a46-dfb2a04246a0/-/preview/180x180/Pirate%20PR%20Avatar.jpg" alt="" loading="lazy" /><strong>{title}</strong><em>{text}</em><small>{href}</small></span></a>;
}

export default function PressKit({ children, className = '', game, footerSteamHref, collection, cover, icon, requestLabel, factGroups, socials, boxArt, squareArt, about, features, closing, videos, artwork, artworkPattern, screenshots, gifs, logos, releases = [] }) {
  const sections = [['details', 'Game details'], ['about', 'About the game'], ['features', 'Key features'], videos.length && ['videos', 'Videos'], ['images', 'Images'], ['artwork', 'Artwork'], ['screenshots', 'Screenshots'], gifs.length && ['gifs', 'GIFs'], ['logos', 'Logos'], releases.length && ['releases', 'Press releases']].filter(Boolean);
  return <div className={`oc-page oc-press ${className}`}>
    {children}
    <header className="pk-topbar">
      <div className="pk-crumbs"><Link to="/" aria-label="Voidmaw home"><img src={wordmark} alt="Voidmaw" /></Link><Link to={game.path}><img src={icon} alt="" width="16" height="16" />{game.title}</Link></div>
      <div className="pk-toplinks"><Link to="/contact">Contact</Link><a className="pk-topbutton" href={game.steamUrl} target="_blank" rel="noopener noreferrer">Steam</a></div>
    </header>
    <div className="pk-cover"><img className="pk-cover-art" src={cover.art} alt={cover.alt} style={{ objectPosition: cover.position }} />{cover.logo && <img className="pk-cover-logo" src={cover.logo} alt={`${game.title} logo`} />}</div>
    <nav className="pk-rail" aria-label="Press kit sections">{sections.map(([id, label]) => <a href={`#${id}`} key={id}><span>{label}</span></a>)}</nav>
    <main className="pk-main">
      <img className="pk-icon" src={icon} alt="" width="124" height="124" />
      <h1>{game.title}</h1>
      <p className="pk-intro">Do you want to create coverage for this game?<br />- <a href={`mailto:contact@voidmaw.com?subject=${encodeURIComponent(`${game.title} press request`)}`}>{requestLabel}</a><br />- <a href={game.steamUrl} target="_blank" rel="noopener noreferrer">View on Steam</a></p>
      <DownloadAll href={collection} />

      <div className="pk-columns">
        <aside aria-labelledby="details">
          <h2 id="details">Game details</h2>
          <a className="pk-art" href={boxArt.original} download><img src={boxArt.src} alt={boxArt.alt} loading="lazy" /></a>
          <div className="pk-facts">
            {factGroups.map((group, index) => <React.Fragment key={index}>
              {group.map(([term, value]) => <p key={term}><strong>{term}</strong><br />{value}</p>)}
              <hr className="pk-rule" />
            </React.Fragment>)}
            <p><strong>Social media</strong><br />{socials.map(([label, href], index) => <React.Fragment key={label}>{index > 0 && ' | '}<a href={href} target="_blank" rel="noopener noreferrer">{label}</a></React.Fragment>)}</p>
          </div>
          <a className="pk-art" href={squareArt.original} download><img src={squareArt.src} alt={squareArt.alt} loading="lazy" /></a>
        </aside>
        <div>
          <h2 id="about">About the game</h2>
          {about.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
          <h2 id="features" className="pk-features-heading">Key features</h2>
          <ul className="pk-features">{features.map(feature => <li key={feature.title}><strong>{feature.title} -</strong> {feature.text}</li>)}</ul>
          {closing && <p>{closing}</p>}
        </div>
      </div>

      <aside className="pk-callout" aria-label="Contacts"><span aria-hidden="true">✉️</span><p><strong>Contacts</strong><br /><strong>Press:</strong> <a href="mailto:contact@voidmaw.com">contact@voidmaw.com</a></p></aside>

      {videos.length > 0 && <SectionHeading id="videos" title="Videos" />}
      <MediaRows items={videos} pattern={[1, 2, 2]} render={Video} />

      <SectionHeading id="images" title="Images" />
      <DownloadAll href={collection} spaced />
      <h3 id="artwork">Artwork</h3>
      <MediaRows items={artwork} pattern={artworkPattern} render={CaptionedAsset} />
      <SectionHeading id="screenshots" title="Screenshots" Tag="h3" />
      <DownloadAll href={collection} />
      <MediaRows items={screenshots} pattern={[2, 1, 2, 1, 1, 2]} render={Asset} />
      {gifs.length > 0 && <SectionHeading id="gifs" title="GIFs" Tag="h3" />}
      <MediaRows items={gifs} pattern={[1, 2]} render={Asset} />
      <SectionHeading id="logos" title="Logos" Tag="h3" />
      <MediaRows items={logos} pattern={[1, 2, 2]} render={CaptionedAsset} />

      {releases.length > 0 && <SectionHeading id="releases" title="Press releases" />}
      {releases.map(Release)}
    </main>
    <SiteFooter steamHref={footerSteamHref} />
  </div>;
}
